import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

type UserRecord = {
  token?: string;
  totalItemPerpage?: number;
};

type LoginPayload = UserRecord & {
  user?: UserRecord;
  admin?: UserRecord;
  token?: string;
};

export interface AuthState {
  users: Record<string, unknown>;
  token: string;
  isLoading: boolean;
  isLogin: boolean;
  stats: unknown[] | Record<string, unknown>;
  paymentVolume: number | Record<string, unknown>;
  totalItemPerpage: number;
}

const initialState: AuthState = {
  users: {},
  token: "",
  isLoading: false,
  isLogin: false,
  stats: [],
  paymentVolume: 0,
  totalItemPerpage: 20,
};

const normalizeItemsPerPage = (
  value: unknown,
  fallback = initialState.totalItemPerpage,
): number => {
  const parsedValue = Number(value);

  if (Number.isFinite(parsedValue) && parsedValue > 0) {
    return parsedValue;
  }

  return fallback;
};

const toUserState = (value: unknown): Record<string, unknown> => {
  if (value && typeof value === "object") {
    return value as Record<string, unknown>;
  }

  return {};
};

const getLoginPayloadUser = (payload: LoginPayload = {}): UserRecord => {
  if (payload.user) {
    return payload.user;
  }

  if (payload.admin) {
    return payload.admin;
  }

  return payload;
};

const getLoginPayloadToken = (payload: LoginPayload = {}): string => {
  if (payload.token) {
    return payload.token;
  }

  if (payload.user?.token) {
    return payload.user.token;
  }

  if (payload.admin?.token) {
    return payload.admin.token;
  }

  return "";
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    login(state, action: PayloadAction<LoginPayload | undefined>) {
      const user = getLoginPayloadUser(action.payload);

      state.users = toUserState(user);
      state.token = getLoginPayloadToken(action.payload);
      state.isLogin = true;
      state.isLoading = false;
      state.totalItemPerpage = normalizeItemsPerPage(
        user.totalItemPerpage,
        state.totalItemPerpage,
      );
    },
    setUserProfile(state, action: PayloadAction<unknown>) {
      const profile = (action.payload ?? {}) as UserRecord;

      state.users = toUserState(profile);
      state.isLogin = true;
      state.totalItemPerpage = normalizeItemsPerPage(
        profile.totalItemPerpage,
        state.totalItemPerpage,
      );
    },
    setStats(
      state,
      action: PayloadAction<AuthState["stats"] | undefined>,
    ) {
      state.stats = action.payload ?? [];
    },
    setPaymentVolume(
      state,
      action: PayloadAction<AuthState["paymentVolume"] | undefined>,
    ) {
      state.paymentVolume = action.payload ?? 0;
    },
    logout() {
      return initialState;
    },
    setTotalItemPerpage(state, action: PayloadAction<number | undefined>) {
      const totalItemPerpage = normalizeItemsPerPage(
        action.payload,
        state.totalItemPerpage,
      );

      state.totalItemPerpage = totalItemPerpage;
      state.users = {
        ...state.users,
        totalItemPerpage,
      };
    },
  },
});

export const {
  login,
  setUserProfile,
  setStats,
  setPaymentVolume,
  logout,
  setTotalItemPerpage,
} = authSlice.actions;

export default authSlice.reducer;
